import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { GraphModalInstance } from '../../../components/GraphModal/GraphModal';
import { GraphModal } from '../../../components/GraphModal/GraphModal';
import { confirmationToast, failureToast } from '../../../components/Toast';
import type { DeleteResponseStatusFragment } from '../../api/message';
import { deleteMessage } from '../../api/message';
import { RemoveContent } from '../../Remove/RemoveContent';

const messages = defineMessages({
  title: { defaultMessage: "Suppression d'un commentaire", id: 'HvcgQe' },
  guide: {
    defaultMessage: 'Etes-vous sûr(e) de vouloir supprimer votre commentaire?',
    id: 'aAu8fh',
  },
  cancel: { defaultMessage: 'garder le commentaire', id: 'vESKuU' },
  remove: { defaultMessage: 'supprimer', id: 'y7ovCd' },
  confirmationLabel: { defaultMessage: 'le message a été supprimé', id: 'AmLURL' },
  failureLabel: { defaultMessage: 'impossible de supprimer le message', id: 'LWM0Jj' },
});

export type MessageRemoverProps = {
  messageId: string;
  threadId?: string;
  content: string;
  onRemove: (data: DeleteResponseStatusFragment) => void;
};
export const MessageRemover: React.FC<PropsWithChildren<MessageRemoverProps>> = ({
  messageId,
  content,
  onRemove,
  children,
}) => {
  const remove = useCallback(async () => {
    try {
      const data = await deleteMessage({ commentId: messageId });
      confirmationToast(<FormattedMessage {...messages.confirmationLabel} />);
      onRemove?.(data);
    } catch {
      failureToast(<FormattedMessage {...messages.failureLabel} />);
    }
  }, [onRemove, messageId]);

  return (
    <GraphModal
      maxWidth={400}
      lazyContent={(tip: GraphModalInstance) => (
        <RemoveContent
          messages={messages}
          cancel={() => tip?.hide()}
          remove={async () => {
            await remove();
            tip?.hide();
          }}
          content={content}
        />
      )}
      showPageOverlay
      forceModal
    >
      {children}
    </GraphModal>
  );
};
